// packages/charts/src/table/formatDateValue.ts
// (moved here from apps/frontend/src/utils/formatDateValue.ts on 2026-09-13,
//  plan one-table-model, B1b; the app file re-exports it)
//
// Grain-aware date formatting for chart X-axes and sparkline tooltips.
// English-only by design — matches the CLAUDE.md "all customer-facing UI
// text in English" rule. (The previous `toLocaleDateString` approach in
// `MetricCardRenderer.tsx` leaked the browser locale into tooltip labels
// — Dutch browsers showed Dutch month names.)
//
// Single source of truth: imported by `MessageRichContent.tsx` (chart
// X-axis labels) and `MetricCardRenderer.tsx` (sparkline tooltips) so both
// surfaces stay format-aligned.

export type TimeGrain =
  | "daily"
  | "weekly"
  | "monthly"
  | "quarterly"
  | "yearly";

export type DateFormatPreset = "short" | "medium" | "long" | "iso";
export type DateOrderPreset = "dmy" | "mdy" | "ymd";

export const MONTH_ABBR = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

export const MONTH_FULL = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/** ISO 8601 week number (Mon-first week, year-aware). */
export function getWeekNumber(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}

/**
 * Format an ISO date value at the given grain using the user's preferred
 * date format and order. Returns the raw input on regex-mismatch (defensive
 * passthrough for legacy / malformed values).
 *
 * `grain` decides the bucket label shape:
 * - daily     → "18 May '26" | "May 18 '26" | "'26 May 18"
 * - weekly    → "Wk20 '26"   | "'26 Wk20"
 * - monthly   → "May '26"    | "'26 May"
 * - quarterly → "Q2 '26"     | "'26 Q2"
 * - yearly    → "'26"
 *
 * `formatPreset="iso"` returns canonical strings ("2026-05-18", "2026-W20").
 */
export function formatDateValue(
  value: string,
  grain: TimeGrain,
  formatPreset: DateFormatPreset = "short",
  orderPreset: DateOrderPreset = "dmy",
): string {
  const dateMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!dateMatch) return value;

  const yearFull = parseInt(dateMatch[1], 10);
  const yearShort = dateMatch[1].slice(2);
  const month = parseInt(dateMatch[2], 10) - 1; // 0-indexed
  const day = parseInt(dateMatch[3], 10);
  const date = new Date(yearFull, month, day);

  if (formatPreset === "iso") {
    if (grain === "weekly") {
      const weekNum = getWeekNumber(date);
      return `${yearFull}-W${weekNum.toString().padStart(2, "0")}`;
    }
    return value.slice(0, 10);
  }

  if (formatPreset === "short") {
    if (grain === "weekly") {
      const weekNum = getWeekNumber(date);
      if (orderPreset === "ymd") return `'${yearShort} Wk${weekNum}`;
      return `Wk${weekNum} '${yearShort}`;
    }
    if (grain === "daily") {
      if (orderPreset === "mdy") return `${MONTH_ABBR[month]} ${day} '${yearShort}`;
      if (orderPreset === "ymd") return `'${yearShort} ${MONTH_ABBR[month]} ${day}`;
      return `${day} ${MONTH_ABBR[month]} '${yearShort}`;
    }
    if (grain === "quarterly") {
      const quarter = Math.floor(month / 3) + 1;
      if (orderPreset === "ymd") return `'${yearShort} Q${quarter}`;
      return `Q${quarter} '${yearShort}`;
    }
    if (grain === "yearly") {
      return `'${yearShort}`;
    }
    // monthly
    if (orderPreset === "ymd") return `'${yearShort} ${MONTH_ABBR[month]}`;
    return `${MONTH_ABBR[month]} '${yearShort}`;
  }

  if (formatPreset === "medium") {
    if (grain === "weekly") {
      const weekNum = getWeekNumber(date);
      if (orderPreset === "ymd") return `${yearFull} Week ${weekNum}`;
      return `Week ${weekNum} ${yearFull}`;
    }
    if (grain === "daily") {
      if (orderPreset === "mdy") return `${MONTH_FULL[month]} ${day}, ${yearFull}`;
      if (orderPreset === "ymd") return `${yearFull} ${MONTH_FULL[month]} ${day}`;
      return `${day} ${MONTH_FULL[month]} ${yearFull}`;
    }
    if (grain === "quarterly") {
      const quarter = Math.floor(month / 3) + 1;
      if (orderPreset === "ymd") return `${yearFull} Q${quarter}`;
      return `Q${quarter} ${yearFull}`;
    }
    if (grain === "yearly") {
      return String(yearFull);
    }
    // monthly
    if (orderPreset === "ymd") return `${yearFull} ${MONTH_FULL[month]}`;
    return `${MONTH_FULL[month]} ${yearFull}`;
  }

  if (formatPreset === "long") {
    if (grain === "weekly") {
      const weekNum = getWeekNumber(date);
      if (orderPreset === "ymd") return `${yearFull}, Week ${weekNum}`;
      return `Week ${weekNum}, ${yearFull}`;
    }
    if (grain === "daily") {
      if (orderPreset === "mdy") return `${MONTH_FULL[month]} ${day}, ${yearFull}`;
      if (orderPreset === "ymd") return `${yearFull}, ${MONTH_FULL[month]} ${day}`;
      return `${day} ${MONTH_FULL[month]} ${yearFull}`;
    }
    if (grain === "quarterly") {
      const quarter = Math.floor(month / 3) + 1;
      if (orderPreset === "ymd") return `${yearFull}, Quarter ${quarter}`;
      return `Quarter ${quarter}, ${yearFull}`;
    }
    if (grain === "yearly") {
      return String(yearFull);
    }
    // monthly
    if (orderPreset === "ymd") return `${yearFull}, ${MONTH_FULL[month]}`;
    return `${MONTH_FULL[month]} ${yearFull}`;
  }

  return value;
}

const VALID_GRAINS: readonly TimeGrain[] = [
  "daily",
  "weekly",
  "monthly",
  "quarterly",
  "yearly",
];

/** Runtime narrow for chart_spec.sparkline_time_grain (defense-in-depth
 *  against corrupt JSON payloads). */
export function isTimeGrain(g: unknown): g is TimeGrain {
  return typeof g === "string" && (VALID_GRAINS as readonly string[]).includes(g);
}
